// Analytics container is the component in charge of verify the current run have cookies policy accepted in order to load the analytics scripts like Google Analytics, Datadog and others.

import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { useCookiePolicy } from '@whistler-ui/k2/hooks';
import { useCallback, useLayoutEffect } from 'react';
import { PropsWithChildren } from 'react';

import { useRenderIf } from './hooks';
import { useTrustArcConsent } from './hooks/useTrustArcConsent';
import { injectTrustArc } from './injectors/trustArcInjector';

type CookieBasedIntegrationsWrapperProps = PropsWithChildren;

const currentEnv = import.meta.env.REACT_APP_SENTRY_ENV;

// Single TrustArc consent-manager domain for GSMA across all regions/envs.
const TRUSTARC_DOMAIN = 'obcbbj';

const dataDogOptionEU: RumInitConfiguration = {
  applicationId: '980c0066-d17b-4086-b8bf-21b6e62dfcd9',
  clientToken: 'pube56f28b762e8440915284830a3b6c2bf',
  site: 'datadoghq.eu',
  service: 'portal-es',
  env: currentEnv,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  defaultPrivacyLevel: 'mask',
  enablePrivacyForActionName: true,
  actionNameAttribute: 'data-dd-action-name',
};

const dataDogOptionUS: RumInitConfiguration = {
  applicationId: '3e4f3758-cec2-4b31-9612-e63903f40697',
  clientToken: 'pub97306a65ece15f439180b8ee7f12c402',
  site: 'datadoghq.com',
  service: 'portal-es-us',
  env: currentEnv,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  defaultPrivacyLevel: 'mask-user-input',
  enablePrivacyForActionName: true,
  actionNameAttribute: 'data-dd-action-name',
};

function initGTM(gtmId: string) {
  if (typeof window === 'undefined') return;

  // Avoid double injection
  if (document.getElementById('gtm-script')) return;

  // Create main script
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.textContent = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // Add <noscript> fallback (optional)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
    gtmId,
  )}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export const CookieBasedIntegrationsWrapper = ({
  children,
}: CookieBasedIntegrationsWrapperProps) => {
  const { isCookiePolicyAccepted } = useCookiePolicy();
  const { isGSMA } = useRenderIf();
  const { acceptedAnyNonNecessary } = useTrustArcConsent({
    autoRefresh: isGSMA,
  });

  // For GSMA, consent is managed by TrustArc: opting in to any non-required
  // category unlocks analytics and app rendering. Other themes fall back to the
  // k2 cookie policy.
  const getCookiePolicyAccepted = useCallback(() => {
    if (isGSMA) {
      return acceptedAnyNonNecessary;
    }
    return isCookiePolicyAccepted;
  }, [acceptedAnyNonNecessary, isCookiePolicyAccepted, isGSMA]);

  useLayoutEffect(() => {
    const isProd = import.meta.env.PROD;
    const isProdOrStg = ['prod', 'stg'].includes(currentEnv);

    // Inject the TrustArc consent manager for GSMA in every environment.
    if (isGSMA) {
      injectTrustArc(TRUSTARC_DOMAIN);
    }

    // Gate analytics on the theme's own consent source: TrustArc for GSMA,
    // the k2 cookie banner elsewhere. GSMA never renders the k2 banner, so
    // checking `isCookiePolicyAccepted` here would block analytics forever.
    if (!getCookiePolicyAccepted()) {
      return;
    }

    if (isProd) {
      datadogRum.init(
        currentEnv.includes('us') ? dataDogOptionUS : dataDogOptionEU,
      );

      if (isGSMA && isProdOrStg) {
        initGTM('GTM-KNXLRB');
      }
    }
  }, [getCookiePolicyAccepted, isGSMA]);

  if (getCookiePolicyAccepted()) {
    return children;
  }

  return null;
};
